import { validarRangoFechas } from '../dateValidation.js'
import { calcPrecioPublicacion } from './matchHelpers.js'

const ENVIO_A_CARGO_COMPRADOR = 3

export const MAPA_PUBLICACIONES = {
  categoriaSeleccionada: {
    filterFn: (p, v) => {
      if (!v) {
        return true
      }
      if (!p.categoriaRoot) {
        return false
      }
      try {
        return JSON.parse(p.categoriaRoot)
          .map((c) => c.id)
          .includes(Number(v))
      } catch {
        return false
      }
    }
  },

  tipoVendedor: {
    titulo: 'Vendedor',
    opciones: ({ perfiles = [] } = {}) =>
      perfiles.map((p) => ({
        label: p.nombre,
        valor: p.idPerfiles
      })),
    filterFn: (p, v) =>
      Array.isArray(v) ? v.includes(Number(p.idPerfil)) : Number(p.idPerfil) === v
  },

  estadoProducto: {
    titulo: 'Estado',
    opciones: () => [
      { label: 'Nuevo', valor: 'nuevo' },
      { label: 'Usado', valor: 'usado' }
    ],
    filterFn: (p, v) => (v === 'nuevo' ? p.nuevo === true : p.nuevo === false)
  },

  estadoPublicacion: {
    titulo: 'Estado de publicación',
    opciones: ({ publicaciones = [] } = {}) => [
      ...new Map(
        publicaciones
          .filter((p) => p.idPublicacionEstado !== null && p.idPublicacionEstado !== undefined)
          .map((p) => [
            p.idPublicacionEstado,
            {
              label: String(p.publicacionEstado ?? p.idPublicacionEstado),
              valor: p.idPublicacionEstado
            }
          ])
      ).values()
    ],
    filterFn: (p, v) => p.idPublicacionEstado === v
  },

  precioDesde: { tipo: 'precio', filterFn: (p, v) => calcPrecioPublicacion(p) >= Number(v) },
  precioHasta: { tipo: 'precio', filterFn: (p, v) => calcPrecioPublicacion(p) <= Number(v) },

  exposicion: {
    titulo: 'Tipo de Exposición',
    opciones: ({ exposiciones = [] } = {}) =>
      exposiciones.map((e) => ({ label: e.nombre, valor: e.idPublicacionExposicion })),
    filterFn: (p, v) => p.idPublicacionExposicion === v
  },

  listaVenta: {
    titulo: 'Listas Ventas',
    opciones: ({ listasVentaConDatos = [] } = {}) =>
      listasVentaConDatos.map((l) => ({ label: l.nombre, valor: l.idListaVenta })),
    filterFn: (p, v) => p.idListaVenta === v
  },

  listaEnvioVencida: {
    titulo: 'Lista Envío Vencida',
    opciones: ({ listasEnviosVencidas = [] } = {}) =>
      listasEnviosVencidas.map((l) => ({ label: l.label, valor: l.value })),
    filterFn: (p, v) => {
      const medio = (p.mediosEnvios ?? []).find((m) => m.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR)
      return medio?.nombreLista?.toString().trim().toLowerCase() === v
    }
  },

  textoBusqueda: {
    placeholder: 'Buscar por título o NRO publicación...',
    filterFn: (p, v) => {
      const val = v.trim().toLowerCase()
      if (!val) return true
      return p.titulo.toLowerCase().includes(val) || p.idPublicacion.toString().includes(val)
    }
  },

  provinciaSeleccionada: {
    titulo: 'Provincia',
    opciones: ({ provincias = [] }) =>
      provincias.map((prov) => ({
        label: prov.charAt(0).toUpperCase() + prov.slice(1),
        valor: prov
      })),
    filterFn: (p, v, filtros) => {
      const domicilios = p.domiciliosVendedor || []
      return domicilios.some((dom) => {
        const filterFnProv = dom.provincia?.toLowerCase() === v
        const filterFnLoc =
          !filtros.localidadSeleccionada ||
          (dom.localidad || dom.ciudad)?.toLowerCase() === filtros.localidadSeleccionada
        return filterFnProv && filterFnLoc
      })
    }
  },

  localidadSeleccionada: {
    titulo: 'Localidad',
    opciones: ({ localidadesFiltradasPorProvincia = [] }) =>
      localidadesFiltradasPorProvincia.map((loc) => ({
        label: loc.charAt(0).toUpperCase() + loc.slice(1),
        valor: loc
      })),
    filterFn: (p, v, filtros) => {
      if (filtros.provinciaSeleccionada) {
        return true
      }
      const domicilios = p.domiciliosVendedor || []
      return domicilios.some((dom) => (dom.localidad || dom.ciudad)?.toLowerCase() === v)
    }
  },

  proveedorPropio: {
    titulo: 'Proveedores propios en e-Buyplace',
    opciones: () => [{ label: 'Sí', valor: 'si' }],
    filterFn: (p, v, filtros) => {
      if (!v) {
        return true
      }
      const ids = (filtros.proveedoresPropios ?? []).map((proveedor) => proveedor.idUsuario)
      return ids.includes(p.idVendedor)
    }
  },

  fechaDesde: {
    filterFn: (p, v) => validarRangoFechas(v, p?.fechaPublicacion ?? p.fechaPublicacion)
  },
  fechaHasta: {
    filterFn: (p, v) => validarRangoFechas(p?.fechaPublicacion ?? p.fechaPublicacion, v)
  },

  filtrosSeleccionados: {
    filterFn: (p, v, filtros) => {
      const { caracteristicasActuales = [], mapEtiquetaIds = {} } = filtros
      const filtrosActivos = Object.entries(v).filter(([, valor]) => valor)
      if (!filtrosActivos.length) {
        return true
      }
      return filtrosActivos.every(([etiqueta, valor]) => {
        const valorNorm = valor.toString().trim().toLowerCase()
        const etiquetaNorm = etiqueta.toString().trim().toLowerCase()
        const idsAsociados = mapEtiquetaIds[etiqueta] || []
        return caracteristicasActuales.some((c) => {
          const filterFnId = idsAsociados.includes(c.idMetaDato)
          const filterFnEtiqueta = c.etiqueta?.toString().trim().toLowerCase() === etiquetaNorm
          const filterFnValor = (c.valor ?? '').toString().trim().toLowerCase() === valorNorm
          return (
            c.idPublicacion === p.idPublicacion && filterFnValor && (filterFnId || filterFnEtiqueta)
          )
        })
      })
    }
  },

  caracteristicasActuales: { filterFn: () => true },
  mapEtiquetaIds: { filterFn: () => true }
}
